import { createAsyncThunk } from '@reduxjs/toolkit';
import authApi from '~/apis/auth';

import { LOGIN } from '~/utils/constants/actionType';
import { WEARER_ROLE_ID } from '~/utils/constants/role';
import { StorageEnum } from '~/utils/enum';
import { ILoginPayload, IRefreshTokenResponse, IResponseLogin, IResponseGetAuthUser } from '~/utils/interface/auth';
import i18n from '~/utils/i18n';
import { IParamsGetUser, IUser } from '~/utils/interface/user';

export const WEARER_WEB_ACCESS_DENIED_ERROR = 'WEARER_WEB_ACCESS_DENIED_ERROR';

const isWearerRoleId = (roleId: unknown) => {
  if (typeof roleId === 'number') return roleId === WEARER_ROLE_ID;
  if (typeof roleId === 'string') return roleId.trim() === String(WEARER_ROLE_ID);
  return false;
};

const clearStoredAuthTokens = () => {
  localStorage.removeItem(StorageEnum.ACCESS_TOKEN);
  localStorage.removeItem(StorageEnum.REFRESH_TOKEN);
};

export const handleLogin = createAsyncThunk<IResponseLogin, ILoginPayload>(
  LOGIN,
  async (data: ILoginPayload, { rejectWithValue }) => {
    try {
      const res = await authApi.login(data);
      const roleId = res.data.roleid;

      if (isWearerRoleId(roleId)) {
        clearStoredAuthTokens();
        return rejectWithValue(WEARER_WEB_ACCESS_DENIED_ERROR);
      }

      const accessToken = res.data.access_token ?? res.data.token;
      const refreshToken = res.data.refresh_token;

      if (!accessToken || !refreshToken) {
        clearStoredAuthTokens();
        return rejectWithValue(i18n.t('login.loginFailed', { ns: 'admin/auth', defaultValue: 'Login failed.' }));
      }

      localStorage.setItem(StorageEnum.ACCESS_TOKEN, accessToken);
      localStorage.setItem(StorageEnum.REFRESH_TOKEN, refreshToken);

      return {
        ...res.data,
        access_token: accessToken,
        refresh_token: refreshToken,
      };
    } catch (error: any) {
      clearStoredAuthTokens();
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const handleLogout = createAsyncThunk<null, void>('AUTH_LOGOUT', async (_, { rejectWithValue }) => {
  try {
    await authApi.logout();
    localStorage.removeItem(StorageEnum.ACCESS_TOKEN);
    localStorage.removeItem(StorageEnum.REFRESH_TOKEN);
    return null;
  } catch (error) {
    return rejectWithValue(error);
  }
});

export const handleGetAuthUserInfo = createAsyncThunk<IResponseGetAuthUser, void>(
  'AUTH_GET_INFO',
  async (_, { rejectWithValue }) => {
    try {
      const res = await authApi.getInfo();
      return res.data;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const handleGetAuthUserProfile = createAsyncThunk<IUser, IParamsGetUser>(
  'AUTH_GET_PROFILE',
  async (params: IParamsGetUser, { rejectWithValue }) => {
    try {
      const res = await authApi.getUserProfile(params);
      return res.data.result;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const handleRefreshToken = createAsyncThunk<IRefreshTokenResponse, void>(
  'AUTH_REFRESH_TOKEN',
  async (_, { rejectWithValue }) => {
    try {
      const refreshToken = localStorage.getItem(StorageEnum.REFRESH_TOKEN);
      if (refreshToken) {
        const res = await authApi.refreshToken({ refresh_token: refreshToken });
        const accessToken = res.data.access_token;
        localStorage.setItem(StorageEnum.ACCESS_TOKEN, accessToken);
        return res.data;
      }
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const handleChangePassword = createAsyncThunk<null, any>(
  'AUTH_CHANGE_PASSWORD',
  async (data: any, { rejectWithValue }) => {
    try {
      const res = await authApi.changePassword(data);
      return res.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);
